/**
 * Relative-time formatting.
 *
 * Three wording flavors preserve every existing UX in the portal:
 *
 *   - `'intl'`   (default) — uses `Intl.RelativeTimeFormat` (currently
 *     hard-coded to English) for correct pluralization and `auto` numeric
 *     phrasing ("5 minutes ago", "yesterday", "2 years ago"). Future
 *     timestamps render as `"in the future"`. The portal is English-only
 *     today; when i18n lands, switch the formatter to runtime locale.
 *
 *   - `'phrase'` — Designer V2 wording, with custom buckets for the
 *     first minute ("just now" <5s, "a few seconds ago" 5–59s). After
 *     24h, falls back to a short calendar date+time so a row from
 *     yesterday doesn't look the same as one from last month.
 *
 *   - `'short'`  — compact monitoring style ("5m ago", "2h ago",
 *     "3d ago"). After 7d, falls back to a short date.
 *
 * Every flavor accepts the same `DateInput` union (string | number |
 * Date | null | undefined) and returns `fallback` (default `''`) for
 * nullish or invalid input.
 *
 * Pass `now` to make calls deterministic in tests (defaults to the
 * current wall clock).
 */

import {
  MS_PER_MINUTE,
  MS_PER_HOUR,
  MS_PER_DAY,
  MS_PER_WEEK,
  MS_PER_MONTH,
  MS_PER_YEAR,
} from './constants';
import { toDate, type DateInput } from './types';

export type RelativeTimeStyle = 'intl' | 'phrase' | 'short';

export interface RelativeTimeOptions {
  /** Wording flavor. Default `'intl'`. */
  style?: RelativeTimeStyle;
  /** Returned for nullish / invalid input. Default `''`. */
  fallback?: string;
  /** Override the "now" reference. Default `Date.now()`. Useful for tests. */
  now?: number;
}

const intlRelativeTimeFormatter = new Intl.RelativeTimeFormat('en', {
  numeric: 'auto',
  style: 'long',
});

export function getRelativeTime(
  value: DateInput,
  options: RelativeTimeOptions = {},
): string {
  const { style = 'intl', fallback = '', now: nowOverride } = options;
  const date = toDate(value);
  if (!date) return fallback;

  const now = nowOverride ?? Date.now();

  switch (style) {
    case 'phrase':
      return formatPhrase(date, now);
    case 'short':
      return formatShort(date, now);
    default:
      return formatIntl(date, now);
  }
}

function formatIntl(date: Date, now: number): string {
  const diffMs = now - date.getTime();
  if (diffMs < 0) return 'in the future';
  if (diffMs < MS_PER_MINUTE) return 'just now';
  if (diffMs < MS_PER_HOUR) {
    return intlRelativeTimeFormatter.format(-Math.floor(diffMs / MS_PER_MINUTE), 'minute');
  }
  if (diffMs < MS_PER_DAY) {
    return intlRelativeTimeFormatter.format(-Math.floor(diffMs / MS_PER_HOUR), 'hour');
  }
  if (diffMs < MS_PER_WEEK) {
    return intlRelativeTimeFormatter.format(-Math.floor(diffMs / MS_PER_DAY), 'day');
  }
  if (diffMs < MS_PER_MONTH) {
    return intlRelativeTimeFormatter.format(-Math.floor(diffMs / MS_PER_WEEK), 'week');
  }
  if (diffMs < MS_PER_YEAR) {
    return intlRelativeTimeFormatter.format(-Math.floor(diffMs / MS_PER_MONTH), 'month');
  }
  return intlRelativeTimeFormatter.format(-Math.floor(diffMs / MS_PER_YEAR), 'year');
}

function formatPhrase(date: Date, now: number): string {
  const diffMs = now - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  if (diffSeconds < 5) return 'just now';
  if (diffSeconds < 60) return 'a few seconds ago';

  if (diffMinutes < 60) {
    return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
  }

  if (diffHours < 24) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }

  // Older than 24h — include calendar date so yesterday's row doesn't
  // look identical to one from last month.
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatShort(date: Date, now: number): string {
  const diffMs = now - date.getTime();
  if (diffMs < MS_PER_MINUTE) return 'just now';
  if (diffMs < MS_PER_HOUR) return `${Math.floor(diffMs / MS_PER_MINUTE)}m ago`;
  if (diffMs < MS_PER_DAY) return `${Math.floor(diffMs / MS_PER_HOUR)}h ago`;
  if (diffMs < MS_PER_WEEK) return `${Math.floor(diffMs / MS_PER_DAY)}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
